import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AtSign, Bell, Check, ShieldAlert, X } from 'lucide-react'
import {
  useGetNotifications,
  useMarkNotificationsSeen,
  useDismissNotification,
} from '../hooks/backend/notifications'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Button } from '../lib/shadcn/button'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import {
  groupByKind,
  targetPath,
  whenLabel,
  type NotificationFeed,
  type NotificationRow,
} from '../utils/notifications'

export default function Notifications() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const [scope, setScope] = useState<'mine' | 'all'>('mine')

  const feedQuery = useGetNotifications<NotificationFeed>()
  const markSeen = useMarkNotificationsSeen()
  const dismiss = useDismissNotification()
  const trigger = feedQuery.trigger

  const load = useCallback(() => {
    void trigger({ scope }, { skipCache: true })
  }, [trigger, scope])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const feed = (feedQuery.data as NotificationFeed | undefined) ?? {
    rows: [],
    unread: 0,
    linked: true,
    can_see_all: false,
  }
  const rows = feed.rows ?? []
  const groups = useMemo(() => groupByKind(rows), [rows])

  // Marking read is deliberately a button and not a side effect of the page
  // loading. A feed that marks itself read on open is a feed you can lose by
  // glancing at it on a phone in a car park, and the whole value of the badge is
  // that it is still there when you get back to a desk.
  const markAll = useCallback(async () => {
    const keys = rows.filter((r) => r.seen_at === null).map((r) => r.notif_key)
    if (keys.length === 0) return
    await markSeen.trigger({ keys })
    load()
  }, [rows, markSeen, load])

  const markOne = useCallback(
    async (row: NotificationRow) => {
      if (row.seen_at !== null) return
      await markSeen.trigger({ keys: [row.notif_key] })
      load()
    },
    [markSeen, load],
  )

  const dismissOne = useCallback(
    async (row: NotificationRow) => {
      await dismiss.trigger({ key: row.notif_key })
      load()
    },
    [dismiss, load],
  )

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Notifications are available to signed-in Fleetly staff.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Bell className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Notifications</h1>
          {feed.unread > 0 && (
            <Chip tone="brand">
              {feed.unread}
              <span className="ml-1 opacity-70">unread</span>
            </Chip>
          )}
          <div className="ml-auto flex items-center gap-2">
            {feed.can_see_all && (
              <div className="inline-flex rounded-md border border-border bg-background p-0.5">
                {(['mine', 'all'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    className={cn(
                      'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                      scope === s
                        ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {s === 'mine' ? 'My book' : 'Whole fleet'}
                  </button>
                ))}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => void markAll()}
              disabled={feed.unread === 0}
            >
              Mark all as read
            </Button>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
        {/* The whole-fleet toggle exists because two people hold every driver.
            Saying so is better than an oversight role opening an empty page and
            concluding that nothing is happening. */}
        {feed.can_see_all && scope === 'mine' && (
          <p className="mt-1 text-xs text-muted-foreground">
            My book is the drivers and cars assigned to you. If none are, everything about the fleet
            is under Whole fleet.
          </p>
        )}
      </div>

      {feedQuery.error && (
        <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          {feedQuery.error}
        </div>
      )}

      {/* NOT LINKED IS NOT THE SAME AS NOTHING TO SHOW. Telling somebody they have
          no notifications when their login was never linked to a person is the
          same lie the interviews module told before migration 0022. */}
      {feed.linked === false && (
        <div className="border-b border-border bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          Your login is not linked to a person on file, so there is nothing to build a feed from.
          This is not the same as having no notifications. Ask an admin to link it in cockpit access.
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {feedQuery.loading && rows.length === 0 && (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Reading your notifications…
          </div>
        )}

        {!feedQuery.loading && rows.length === 0 && feed.linked !== false && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Nothing outstanding. Nobody has named you, nothing on your tasks has moved, and there
            are no open gaps in your book.
          </div>
        )}

        {groups.map((group) => (
          <section key={group.kind} className="border-b border-border last:border-b-0">
            <header className="sticky top-0 z-10 flex items-baseline gap-2 border-b border-border bg-muted/60 px-4 py-2 backdrop-blur">
              <h2 className="text-sm font-semibold text-foreground">{group.label}</h2>
              <span className="text-xs tabular-nums text-muted-foreground">
                {group.rows.length}
                {group.unread > 0 ? `, ${group.unread} unread` : ''}
              </span>
              {group.means && (
                <span className="ml-2 hidden truncate text-[11px] text-muted-foreground sm:inline">
                  {group.means}
                </span>
              )}
            </header>
            <ul>
              {group.rows.map((row) => {
                const path = targetPath(row)
                const unread = row.seen_at === null
                const body = (
                  <>
                    <div className="flex items-start gap-2">
                      {unread ? (
                        <span
                          aria-label="Unread"
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--brand))]"
                        />
                      ) : (
                        <span className="mt-1.5 h-2 w-2 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            'text-sm',
                            unread ? 'font-medium text-foreground' : 'text-muted-foreground',
                          )}
                        >
                          {row.kind === 'mention' && (
                            <AtSign className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
                          )}
                          {row.title}
                        </p>
                        {row.detail && (
                          <p className="mt-0.5 text-xs text-muted-foreground">{row.detail}</p>
                        )}
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {row.category === 'standing' ? 'True now' : whenLabel(row.occurred_at)}
                        </p>
                      </div>
                    </div>
                  </>
                )
                return (
                  <li
                    key={row.notif_key}
                    className={cn(
                      'flex items-start gap-2 border-b border-border/60 px-4 py-3 last:border-b-0',
                      unread && 'bg-[hsl(var(--brand-soft))]/20',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      {path ? (
                        <Link
                          to={path}
                          onClick={() => void markOne(row)}
                          className="block rounded hover:opacity-80"
                        >
                          {body}
                        </Link>
                      ) : (
                        // No honest destination. Rendered as text rather than as
                        // a link that lands somewhere with no way to find the
                        // thing it was about.
                        <div>{body}</div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {unread && (
                        <button
                          type="button"
                          onClick={() => void markOne(row)}
                          title="Mark as read"
                          aria-label="Mark as read"
                          className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void dismissOne(row)}
                        title="Dismiss. A standing one comes back if the situation underneath it changes."
                        aria-label="Dismiss"
                        className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
